import { useMemo } from 'react';
import { InfoChipProps } from 'src/components/cards/item-card/rental-chips/InfoChip';
import { RentalChipsProps } from 'src/components/cards/item-card/rental-chips/RentalChips';
import { useServerTime } from 'src/contexts/useServerTime';
import { useDateFormatter } from './useDateFormatter';
import { getRentalDuration } from 'src/util/getRentalDuration';

export type UseRentalTagsOptions = Omit<RentalChipsProps, 'showAsWhitelisted'>;

export type RentalChip = Omit<InfoChipProps, 'showAsWhitelisted'>;

export const useRentalChips = ({
  hoursMax,
  hoursMin,
  rentedUntil,
  royalty,
  forTournaments,
}: UseRentalTagsOptions) => {
  const { now } = useServerTime();
  const dateFormatter = useDateFormatter();

  const currentlyRented = useMemo(() => {
    return !!rentedUntil && rentedUntil.getTime() > now();
  }, [rentedUntil, now]);

  const chips: RentalChip[] = useMemo(() => {
    if (currentlyRented) {
      return [
        {
          label: 'Rented',
          tooltipTitle: `This item has been rented. It will be available for rent again after ${dateFormatter(
            rentedUntil!,
          )}.`,
        },
      ];
    }
    const duration = getRentalDuration(hoursMin, hoursMax);
    const result: RentalChip[] = [
      {
        label: duration,
        tooltipTitle: `This item can be rented for ${duration}.`,
      },
    ];
    if (royalty) {
      result.push({
        label: `${royalty}%`,
        tooltipTitle: `The lender of this item will receieve ${royalty}% of your in-game earnings.`,
      });
    }
    if (forTournaments) {
      result.push({
        label: 'Tournament',
        tooltipTitle:
          'This item can be rented for one or more upcoming tournaments.',
      });
    }
    return result;
  }, [
    currentlyRented,
    hoursMin,
    hoursMax,
    royalty,
    forTournaments,
    dateFormatter,
    rentedUntil,
  ]);

  return { chips, currentlyRented };
};
